import { Suspense } from 'react';

import { GridTileImage } from '@frontend/storefront/components/grid/tile';
import Footer from '@frontend/storefront/components/layout/footer';
import { Gallery } from '@frontend/storefront/components/product/gallery';
import { ProductDescription } from '@frontend/storefront/components/product/product-description';
import { useStorefrontData } from '@frontend/storefront/data';
import type { Image } from '@frontend/storefront/lib/shopify/types';
import Link from '@frontend/storefront/Link';

export default function ProductPage() {
  const { product } = useStorefrontData();
  if (!product) return null;

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: product.description,
    image: product.featuredImage?.url,
    offers: {
      '@type': 'AggregateOffer',
      availability: product.availableForSale
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      priceCurrency: product.priceRange.minVariantPrice.currencyCode,
      highPrice: product.priceRange.maxVariantPrice.amount,
      lowPrice: product.priceRange.minVariantPrice.amount,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(productJsonLd).replace(/</g, '\\u003c'),
        }}
      />
      <div className="np-section">
        <div className="np-container">
          <div className="grid grid-cols-1 items-start gap-8 rounded-np-lg bg-np-cream p-4 desk:grid-cols-2 desk:gap-12 desk:p-[26px]">
            <Suspense
              fallback={
                <div className="aspect-square w-full rounded-np-lg bg-np-card" />
              }
            >
              <Gallery images={galleryImages(product)} />
            </Suspense>

            <div className="px-2 pb-4 desk:px-0 desk:py-10 desk:pr-[39px]">
              <Suspense fallback={null}>
                <ProductDescription product={product} />
              </Suspense>
            </div>
          </div>
        </div>
        <RelatedProducts />
      </div>
      <Footer />
    </>
  );
}

// Until the product has its own photos in Shopify, show the homepage hero
// and the sample book covers.
const fallbackImages = [
  {
    src: '/home/hero.jpg',
    altText: 'A boy reading his personalized nopad book in bed',
  },
  ...['Our friendship', 'Stop, picture, go', 'Green flag', 'Stand tall'].map(
    (title, i) => ({ src: `/home/book-${i + 1}.webp`, altText: title }),
  ),
];

function galleryImages(product: { images: Image[] }) {
  if (!product.images.length) return fallbackImages;

  return product.images.slice(0, 5).map((image) => ({
    src: image.url,
    altText: image.altText,
  }));
}

function RelatedProducts() {
  const relatedProducts = useStorefrontData().products ?? [];

  if (!relatedProducts.length) return null;

  return (
    <div className="np-container py-8">
      <h2 className="mb-6 text-2xl leading-[1.2] font-bold tracking-[-0.3px]">
        You might also like
      </h2>
      <ul className="flex w-full gap-4 overflow-x-auto pt-1">
        {relatedProducts.map((product) => (
          <li
            key={product.handle}
            className="aspect-square w-full flex-none min-[475px]:w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5"
          >
            <Link
              className="relative h-full w-full"
              href={`/product/${product.handle}`}
              prefetch={true}
            >
              <GridTileImage
                alt={product.title}
                label={{
                  title: product.title,
                  amount: product.priceRange.maxVariantPrice.amount,
                  currencyCode: product.priceRange.maxVariantPrice.currencyCode,
                }}
                src={product.featuredImage?.url}
                fill
                sizes="(min-width: 1024px) 20vw, (min-width: 768px) 25vw, (min-width: 640px) 33vw, (min-width: 475px) 50vw, 100vw"
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
